// LEVELS, AREAS & SYSTEMS - PRINT AND EXCEL (owner, 2026-10-13: "i need the
// levels & areas & systems, with their sheet and with their legend and
// subsystem per legend to be exported and printed, create a button to print
// and a button export excel data").
//
// One model, two outputs. Per LEVEL: its drawing (sheet name, page,
// revision, image) with the zones drawn on it, the legend (colour ->
// meaning), each AREA with the systems tagged in it on that level, and the
// SUBSYSTEMS - the zones that stand for a legend entry (Hydro - North,
// K-Line... on BA03), each with the areas it runs through and its own
// systems. Counts are tagged elements on that level (GET
// /areas/systems/by-level), so the same zone shown on two levels prints
// two different sets of numbers.
//
// Pure: everything comes in as arguments, so the rows can be tested
// without a page.

const SYSTEM_ORDER = ["Gear", "Equipment", "Busway", "Racks", "Cable tray", "Single line", "Branch", "Conduits (all sizes)", "Electrical fixtures", "Lighting fixtures", "Data devices"];
const sysRank = (n) => { const i = SYSTEM_ORDER.indexOf(n); return i < 0 ? SYSTEM_ORDER.length : i; };

const hex = (c) => {
  const m = /^#?([0-9a-f]{6})/i.exec(String(c || ""));
  if (!m) return null;
  const v = parseInt(m[1], 16);
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
};
const colourDistance = (a, b) => {
  const x = hex(a); const y = hex(b);
  if (!x || !y) return Infinity;
  return Math.sqrt((x[0] - y[0]) ** 2 + (x[1] - y[1]) ** 2 + (x[2] - y[2]) ** 2);
};
const norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, " ").trim();

/**
 * The legend entry a zone stands for: by NAME first ("Hydro - North (IFF
 * SIGN-OFF 03-05-27)" names the zone "Hydro - North"), then by the nearest
 * colour within a small distance - a zone painted by hand is rarely the
 * legend's exact hex; a near colour is not taken as the same entry. null
 * when neither says anything.
 */
export function legendFor(zone, entries) {
  const list = entries || [];
  const name = norm(zone && zone.name);
  if (name) {
    // Longest meaning first, so "L2 K-Line" beats "K-Line" for a zone named "L2 K-Line".
    const byName = list
      .filter((e) => { const m = norm(e.meaning); return m === name || m.startsWith(`${name} (`) || m.startsWith(`${name} -`) || m.split(" (")[0] === name; })
      .sort((a, b) => norm(b.meaning).length - norm(a.meaning).length)[0];
    if (byName) return byName;
  }
  // Colour only when it is practically the same paint: zones drawn by hand
  // land a few shades off, and on BA03 Area A's blue sits 26 away from the
  // legend's "DPE Priority Areas" - near is not the same.
  let best = null; let bestD = 12;
  for (const e of list) { const d = colourDistance(zone && zone.color, e.color); if (d < bestD) { best = e; bestD = d; } }
  return best;
}

// Point in polygon, percent coordinates.
function inside(p, poly) {
  let c = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]; const b = poly[j];
    if (((a.y > p.y) !== (b.y > p.y)) && (p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x)) c = !c;
  }
  return c;
}
const centroid = (poly) => poly.reduce((s, p) => ({ x: s.x + p.x / poly.length, y: s.y + p.y / poly.length }), { x: 0, y: 0 });
/** Do two zones drawn on the same image overlap? (a vertex or the centre of one inside the other) */
export function overlaps(a, b) {
  const pa = (a && a.points) || []; const pb = (b && b.points) || [];
  if (pa.length < 3 || pb.length < 3) return false;
  if (a.sheetImageUrl && b.sheetImageUrl && a.sheetImageUrl !== b.sheetImageUrl) return false;
  // Corners pulled 5% toward the centre: zones drawn edge to edge (A next to
  // B) share a boundary, and a shared boundary is not an overlap.
  const shrink = (poly) => { const c = centroid(poly); return poly.map((p) => ({ x: p.x + (c.x - p.x) * 0.05, y: p.y + (c.y - p.y) * 0.05 })); };
  const sa = shrink(pa); const sb = shrink(pb);
  return sa.some((p) => inside(p, pb)) || sb.some((p) => inside(p, pa)) || inside(centroid(pa), pb) || inside(centroid(pb), pa);
}

// nearest: the part of each count placed by NEAREST AREA (above the top
// floor, outside every outline or with no position of its own) - shown as such.
const systemsList = (counts, nearest = {}) => Object.entries(counts || {})
  .filter(([, n]) => n > 0)
  // "Conduits" is the whole - single line and branch together - so it says so.
  .map(([name, elements]) => ({ name: name === "Conduits" ? "Conduits (all sizes)" : name, elements, nearest: (nearest && nearest[name]) || 0 }))
  .sort((x, y) => sysRank(x.name) - sysRank(y.name) || y.elements - x.elements);

/**
 * -> [{ level, levelId, sheet: { name, page, revision, imageUrl }, legend: [entries used], zones: [...for drawing],
 *       areas: [{ name, color, legend, subsystems: [names], systems: [{name, elements}] }],
 *       subsystems: [{ name, color, legend, areas: [names], systems }], tagged: n }]
 *   pages     level pages (GET /areas/levels: _id, name, areaIds, sheetDocControlId, sheetPage, sheetImageUrl)
 *   zones     every zone (GET /areas)
 *   legend    GET /areas/legend ({ entries })
 *   sheets    GET /areas/sheets ({ _id, name, revisionNumber, pages })
 *   byLevel   GET /areas/systems/by-level ({ pages: { pageId: { areaId: { system: n } } } })
 */
export function buildLevelsExport({ pages, zones, legend, sheets, byLevel }) {
  const entries = (legend && legend.entries) || [];
  const zoneById = new Map((zones || []).map((z) => [String(z._id), z]));
  const sheetById = new Map((sheets || []).map((s) => [String(s._id), s]));
  return (pages || []).map((p) => {
    const levelZones = (p.areaIds || []).map((id) => zoneById.get(String(id))).filter(Boolean);
    const counts = (byLevel && byLevel.pages && byLevel.pages[String(p._id)]) || {};
    const nearestCounts = (byLevel && byLevel.nearest && byLevel.nearest[String(p._id)]) || {};
    const sh = p.sheetDocControlId ? sheetById.get(String(p.sheetDocControlId)) : null;
    const withLegend = levelZones.map((z) => ({ z, legend: legendFor(z, entries) }));
    const subs = withLegend.filter((x) => x.legend);
    const mains = withLegend.filter((x) => !x.legend);
    const areas = mains
      .map(({ z }) => ({
        id: String(z._id), name: z.name, color: z.color || "", legend: null,
        subsystems: subs.filter((s) => overlaps(z, s.z)).map((s) => s.z.name),
        systems: systemsList(counts[String(z._id)], nearestCounts[String(z._id)]),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    const subsystems = subs
      .map(({ z, legend: e }) => ({
        id: String(z._id), name: z.name, color: z.color || "", legend: e,
        areas: mains.filter((m) => overlaps(m.z, z)).map((m) => m.z.name).sort(),
        systems: systemsList(counts[String(z._id)], nearestCounts[String(z._id)]),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    const used = [];
    subsystems.forEach((s) => { if (s.legend && !used.includes(s.legend)) used.push(s.legend); });
    const tagged = [...areas, ...subsystems].reduce((n, x) => n + x.systems.reduce((m, s) => m + s.elements, 0), 0);
    return {
      level: p.name, levelId: String(p._id),
      sheet: { name: sh ? sh.name : "", page: (Number(p.sheetPage) || 0) + 1, revision: sh && sh.revisionNumber != null ? sh.revisionNumber : "", imageUrl: p.sheetImageUrl || "" },
      legend: entries, legendUsed: used,
      zones: levelZones.map((z) => ({ id: String(z._id), name: z.name, color: z.color || "#3b82f6", points: z.points || [] })),
      areas, subsystems, tagged,
    };
  });
}

/** The "Levels & Areas" sheet: one row per level / area or subsystem / system. */
export function exportRows(model) {
  const rows = [];
  for (const L of model || []) {
    const sheet = { "Sheet": L.sheet.name, "Sheet page": L.sheet.page, "Sheet revision": L.sheet.revision };
    const push = (o, systems) => {
      if (!systems.length) rows.push({ ...o, "System": "", "Tagged elements": 0, "Placed by nearest area": 0, ...sheet });
      systems.forEach((s) => rows.push({ ...o, "System": s.name, "Tagged elements": s.elements, "Placed by nearest area": s.nearest || 0, ...sheet }));
    };
    for (const a of L.areas) push({ "Level": L.level, "Area": a.name, "Legend colour": "", "Legend meaning": "", "Subsystem": "", "Subsystems in this area": a.subsystems.join(", ") }, a.systems);
    for (const s of L.subsystems) push({ "Level": L.level, "Area": s.areas.join(", "), "Legend colour": s.legend ? s.legend.color : s.color, "Legend meaning": s.legend ? s.legend.meaning : "", "Subsystem": s.name, "Subsystems in this area": "" }, s.systems);
  }
  return rows;
}

/** Writes the workbook: "Levels & Areas", one sheet per level, and "Legend". */
export function downloadWorkbook(XLSX, model, { projectName = "Project", date = new Date() } = {}) {
  const wb = XLSX.utils.book_new();
  const all = exportRows(model);
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(all.length ? all : [{ Level: "No levels" }]), "Levels & Areas");
  const used = new Set();
  for (const L of model) {
    let name = String(L.level || "Level").replace(/[\\/?*[\]:]/g, " ").slice(0, 31) || "Level";
    while (used.has(name)) name = `${name.slice(0, 28)} ${used.size}`;
    used.add(name);
    const rows = exportRows([L]);
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows.length ? rows : [{ Level: L.level, Area: "No areas on this level" }]), name);
  }
  const legend = ((model[0] && model[0].legend) || []).map((e) => ({ "Colour": e.color, "Meaning": e.meaning, "Discipline": e.discipline || "" }));
  if (legend.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(legend), "Legend");
  const day = new Date(date).toISOString().slice(0, 10);
  const file = `${String(projectName).replace(/[\\/:*?"<>|]+/g, " ").trim()} Levels Areas Systems ${day}.xlsx`;
  XLSX.writeFile(wb, file);
  return file;
}

const esc = (s) => String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const swatch = (c) => `<span style="display:inline-block;width:11px;height:11px;border-radius:2px;border:1px solid rgba(0,0,0,.2);background:${esc(c || "#ccc")};vertical-align:-1px;margin-right:4px"></span>`;
const sysText = (systems) => (systems.length
  ? systems.map((s) => `${esc(s.name)} <b>${s.elements.toLocaleString()}</b>${s.nearest ? ` <span class="near">(${s.nearest.toLocaleString()} by nearest area)</span>` : ""}`).join(" &middot; ")
  : "<i>no tagged elements</i>");

/** A print-ready document: one level per page, drawing + outlines + legend, then the table. */
export function printHtml(model, { projectName = "", origin = "" } = {}) {
  const abs = (u) => (/^https?:/i.test(u) || !u ? u : `${origin}${u.startsWith("/") ? "" : "/"}${u}`);
  const pages = model.map((L) => {
    const poly = L.zones.filter((z) => z.points.length >= 3).map((z) => {
      const pts = z.points.map((p) => `${p.x},${p.y}`).join(" ");
      const c = centroid(z.points);
      return `<polygon points="${pts}" fill="${esc(z.color)}" fill-opacity="0.28" stroke="${esc(z.color)}" stroke-width="0.25" vector-effect="non-scaling-stroke"/>`
        + `<text x="${c.x}" y="${c.y}" font-size="1.6" text-anchor="middle" fill="#111" stroke="#fff" stroke-width="0.25" paint-order="stroke">${esc(z.name)}</text>`;
    }).join("");
    const drawing = L.sheet.imageUrl
      ? `<div class="sheet"><img src="${esc(abs(L.sheet.imageUrl))}" alt=""><svg viewBox="0 0 100 100" preserveAspectRatio="none">${poly}</svg></div>`
      : `<div class="nosheet">No drawing on this level yet.</div>`;
    const legendRows = (L.legend || []).map((e) => `<div>${swatch(e.color)}${esc(e.meaning)}</div>`).join("") || "<i>No legend on this project.</i>";
    const areaRows = L.areas.map((a) => `<tr><td><b>${esc(a.name)}</b></td><td>${swatch(a.color)}</td><td>${esc(a.subsystems.join(", ")) || "-"}</td><td>${sysText(a.systems)}</td></tr>`).join("");
    const subRows = L.subsystems.map((s) => `<tr><td><b>${esc(s.name)}</b></td><td>${swatch(s.legend ? s.legend.color : s.color)}${esc(s.legend ? s.legend.meaning : "")}</td><td>${esc(s.areas.join(", ")) || "-"}</td><td>${sysText(s.systems)}</td></tr>`).join("");
    return `<section>
      <h1>${esc(projectName)} &middot; ${esc(L.level)}</h1>
      <div class="meta">${L.sheet.name ? `Sheet: ${esc(L.sheet.name)}, page ${esc(L.sheet.page)}${L.sheet.revision !== "" ? `, revision ${esc(L.sheet.revision)}` : ""}` : "No sheet chosen for this level"} &middot; ${L.tagged.toLocaleString()} tagged elements</div>
      <div class="top">${drawing}<div class="legend"><h3>Legend</h3>${legendRows}</div></div>
      <h3>Areas and their systems</h3>
      ${areaRows ? `<table><tr><th>Area</th><th></th><th>Subsystems in it</th><th>Systems (tagged elements)</th></tr>${areaRows}</table>` : "<p><i>No areas on this level.</i></p>"}
      ${subRows ? `<h3>Subsystems by legend</h3><table><tr><th>Subsystem</th><th>Legend</th><th>Areas it runs through</th><th>Systems (tagged elements)</th></tr>${subRows}</table>` : ""}
      ${[...L.areas, ...L.subsystems].some((x) => x.systems.some((s) => s.nearest)) ? `<div class="note">"By nearest area": elements above the top floor that sit outside every area outline, or have no position of their own in the model, counted in the nearest area of this level.</div>` : ""}
    </section>`;
  }).join("");
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(projectName)} - Levels, Areas & Systems</title>
<style>
  @page { size: landscape; margin: 10mm; }
  body { font-family: Arial, sans-serif; color: #111; margin: 0; }
  section { page-break-after: always; padding: 4mm; }
  section:last-child { page-break-after: auto; }
  h1 { font-size: 18px; margin: 0 0 2px; } h3 { font-size: 13px; margin: 10px 0 4px; }
  .meta { color: #555; font-size: 11px; margin-bottom: 6px; }
  .top { display: flex; gap: 10px; align-items: flex-start; }
  .sheet { position: relative; flex: 1 1 auto; max-width: 78%; border: 1px solid #ddd; }
  .sheet img { display: block; width: 100%; }
  .sheet svg { position: absolute; inset: 0; width: 100%; height: 100%; }
  .nosheet { flex: 1; padding: 30px; color: #888; border: 1px dashed #ccc; }
  .legend { flex: 0 0 21%; font-size: 10.5px; line-height: 1.6; }
  table { border-collapse: collapse; width: 100%; font-size: 10.5px; page-break-inside: auto; }
  tr { page-break-inside: avoid; }
  th, td { border: 1px solid #ddd; padding: 3px 5px; text-align: left; vertical-align: top; }
  th { background: #f2f2f7; }
  .near { color: #8a5a00; font-size: 9.5px; }
  .note { color: #555; font-size: 10px; margin-top: 6px; }
</style></head><body>${pages || "<p>No levels on this project.</p>"}</body></html>`;
}
